/**
 * optionsState — pure section model for the OPTIONS screen (M3).
 * Kept DOM-free (bootAdvance.ts pattern) so routing/cycling is unit-testable
 * in the node vitest environment.
 */

export type OptionsSectionId = "controls" | "video" | "audio" | "account";

export interface OptionsSectionDef {
  id: OptionsSectionId;
  label: string;
  sub: string;
}

export const OPTIONS_SECTIONS: readonly OptionsSectionDef[] = [
  { id: "controls", label: "CONTROLS", sub: "BINDINGS · CONTROL METHOD · PAD TEST" },
  { id: "video", label: "VIDEO", sub: "RENDER MODE · CRT EFFECTS" },
  { id: "audio", label: "AUDIO", sub: "GAME SFX · UI BLIPS · ANNOUNCER" },
  { id: "account", label: "ACCOUNT", sub: "SIGN IN · STATS · SIGN OUT" },
];

export function isOptionsSection(v: unknown): v is OptionsSectionId {
  return v === "controls" || v === "video" || v === "audio" || v === "account";
}

/** show("options", params) → section to open. Defaults to CONTROLS. */
export function resolveOptionsSection(params: unknown): OptionsSectionId {
  const section = (params as { section?: unknown } | null | undefined)?.section;
  return isOptionsSection(section) ? section : "controls";
}

export function sectionIndex(id: OptionsSectionId): number {
  return OPTIONS_SECTIONS.findIndex((s) => s.id === id);
}

/** Cycle with wrap-around (rail up/down). */
export function cycleOptionsSection(cur: OptionsSectionId, delta: number): OptionsSectionId {
  const n = OPTIONS_SECTIONS.length;
  const i = sectionIndex(cur);
  const next = (((i + delta) % n) + n) % n;
  return OPTIONS_SECTIONS[next]!.id;
}

/** Volume slider stepping for pad left/right: ±10 % clamped to [0, 1]. */
export function stepVolume(cur: number, delta: 1 | -1): number {
  const next = Math.round((cur + delta * 0.1) * 10) / 10;
  return Math.max(0, Math.min(1, next));
}
